import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : 'http://localhost:8001/api';

const VerifyAccount = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('');
  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setStatus('error');
      setMessage('Invalid verification link - no token provided');
    }
  }, [token]);

  const verifyEmail = async () => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link');
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/verify-email?token=${token}`);
      
      if (response.data.already_verified) {
        setStatus('success');
        setMessage('Your email is already verified. You can log in now.');
      } else {
        setStatus('success');
        setMessage(response.data.message || 'Email verified successfully!');
      }
    } catch (error) {
      setStatus('error');
      setMessage(
        error.response?.data?.detail || 
        'Verification link has expired or is invalid. Please request a new verification email.'
      );
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem'
    },
    card: {
      width: '100%',
      maxWidth: '28rem',
      padding: '2.5rem',
      backgroundColor: 'white',
      borderRadius: '1rem',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
    },
    logo: {
      textAlign: 'center',
      marginBottom: '2rem',
      fontSize: '2rem',
      fontWeight: 'bold',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent'
    },
    button: {
      width: '100%',
      padding: '0.875rem 1.5rem',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      marginTop: '1.5rem',
      transition: 'transform 0.2s'
    },
    outlineButton: {
      width: '100%',
      padding: '0.875rem 1.5rem',
      backgroundColor: 'white',
      color: '#667eea',
      border: '2px solid #667eea',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      marginTop: '1rem',
      transition: 'all 0.2s'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logo}>Skilledge</div>

        <div style={{ textAlign: 'center' }}>
          {status === 'verifying' && (
            <>
              <Loader2 style={{ width: '4rem', height: '4rem', color: '#667eea', margin: '0 auto 1.5rem' }} className="animate-spin" />
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                Verifying Your Account
              </h2>
              <p style={{ color: '#6b7280' }}>Please wait...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div style={{
                width: '5rem',
                height: '5rem',
                margin: '0 auto 1.5rem',
                borderRadius: '50%',
                backgroundColor: '#dcfce7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CheckCircle style={{ width: '3rem', height: '3rem', color: '#16a34a' }} />
              </div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                Account Verified! 🎉
              </h2>
              <p style={{ color: '#6b7280', marginBottom: '0.5rem' }}>{message}</p>
              <p style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                You can now log in and explore Skilledge.
              </p>
              
              <button 
                onClick={() => navigate('/login')} 
                style={styles.button}
                onMouseOver={(e) => e.target.style.transform = 'scale(1.02)'}
                onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
              >
                Go to Login
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <div style={{
                width: '5rem',
                height: '5rem',
                margin: '0 auto 1.5rem',
                borderRadius: '50%',
                backgroundColor: '#fee2e2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <XCircle style={{ width: '3rem', height: '3rem', color: '#dc2626' }} />
              </div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                Verification Failed
              </h2>
              <p style={{ color: '#6b7280', marginBottom: '1rem' }}>{message}</p>
              
              <div style={{
                backgroundColor: '#fef3c7',
                border: '1px solid #fbbf24',
                borderRadius: '0.5rem',
                padding: '1rem',
                marginBottom: '1rem',
                textAlign: 'left'
              }}>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <Mail style={{ width: '1.25rem', height: '1.25rem', color: '#ca8a04', flexShrink: 0 }} />
                  <div style={{ fontSize: '0.875rem' }}>
                    <p style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Need a new verification link?</p>
                    <p style={{ color: '#6b7280' }}>Go to the login page and click "Resend Verification Email"</p>
                  </div>
                </div>
              </div>
              
              <button 
                onClick={() => navigate('/login')} 
                style={styles.outlineButton}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#f3f4f6';
                  e.target.style.transform = 'scale(1.02)';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                Go to Login
              </button>
            </>
          )}
        </div>

        <div style={{ marginTop: '2rem', textAlign: 'center', fontSize: '0.75rem', color: '#9ca3af' }}>
          © 2025 Skilledge. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default VerifyAccount;
